import {
    AdjustmentCodes,
    Amount,
    BaseButton,
    CurrencyType,
    InvoiceDraftCreateDiscount,
    InvoiceDraftCreateLineItem,
    ProviderClaimPaymentItem,
    ProviderCollectionInvoiceDraft,
    ProviderCollectionInvoiceDraftLineItemWithPaymentsItem,
    ProviderCollectionInvoiceDraftPreview,
    RemarkCodes,
    ToastContext,
    Variables,
    formatPayerAdjustmentGroupShortCode,
    isPayerAdjustmentGroupType,
    normalizePayerAdjustmentGroup,
    renderAmount,
    renderDate,
    Tooltip,
} from '@enter/core';
import {
    mdiArrowRight,
    mdiCallSplit,
    mdiCancel,
    mdiCash,
    mdiChevronDown,
    mdiChevronRight,
    mdiClose,
    mdiDeleteOutline,
    mdiFlagOutline,
    mdiInformationOutline,
    mdiLinkOff,
    mdiOpenInNew,
    mdiPlus,
    mdiRefresh,
    mdiSkipNext,
    mdiTagOutline,
    mdiTune,
} from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { CollectionAccountPayment, IObservedRootStore } from 'stores';
import * as S from './resolve-invoice-dialog.styles';

type LineEdit = {
    serviceDate?: string;
    procedureCpt?: string;
    procedureCptDescription?: string;
    procedureCharge?: Amount;
    payerPayments: string;
    payerAdjustments: string;
    patientResponsibility: string;
    patientPayments: string;
    patientDiscounts: string;
};

type DiscountEdit = {
    id: string;
    serviceDate?: string;
    discountCpt: string;
    discountCptDescription: string;
    discountAmount: string;
};

type DiscountField = Exclude<keyof DiscountEdit, 'id' | 'serviceDate'>;

type IssueTone = 'warn' | 'bad' | 'info';

type ResolveIssue = {
    icon: string;
    label: string;
    detail: string;
    tone: IssueTone;
};

type Props = IObservedRootStore & {
    draft: ProviderCollectionInvoiceDraft;
    onClose: () => void;
    onResolved: (opts?: { advance?: boolean }) => void | Promise<void>;
};

const usd = (n: number): Amount => ({ amount: n.toFixed(2), currency: CurrencyType.USD });

const toNum = (v: string | undefined): number => {
    const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) ? n : 0;
};

const fromAmount = (a?: Amount): string => (a?.amount != null ? Number(a.amount).toFixed(2) : '0.00');

const fmtMoney = (n: number): string => renderAmount(usd(n));

const nextDiscountId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `disc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const round2 = (n: number): number => Math.round(n * 100) / 100;

const paymentCap = (p: CollectionAccountPayment): number =>
    p.allocatedToThisClaim ? round2(p.amount + p.unallocatedAmount) : round2(p.unallocatedAmount);

/** Posted to this claim already — Confirm cannot deallocate, so the UI must not go below this. */
const paymentFloor = (p: CollectionAccountPayment): number =>
    p.allocatedToThisClaim ? round2(p.amount) : 0;

const clampUseOnInvoice = (p: CollectionAccountPayment, raw: number): number =>
    round2(Math.max(paymentFloor(p), Math.min(paymentCap(p), raw)));

const ALLOC_FOCUSABLE =
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const ALLOC_TITLE_ID = 'resolve-invoice-alloc-title';
const ALLOC_DESC_ID = 'resolve-invoice-alloc-desc';

const paymentElsewhere = (p: CollectionAccountPayment): number => {
    const onThis = p.allocatedToThisClaim ? p.amount : 0;
    return Math.max(0, round2(p.sourceTotal - p.unallocatedAmount - onThis));
};

const redistributePatientPaid = (rows: LineEdit[], pool: number): LineEdit[] => {
    const tot = rows.reduce((t, l) => t + toNum(l.patientResponsibility), 0);
    let left = round2(pool);
    return rows.map((l, i) => {
        const lineCap = Math.max(0, toNum(l.patientResponsibility) - toNum(l.patientDiscounts));
        let v =
            i === rows.length - 1
                ? left
                : tot > 0
                  ? round2(pool * (toNum(l.patientResponsibility) / tot))
                  : 0;
        v = Math.max(0, Math.min(v, lineCap, left));
        left = round2(left - v);
        return { ...l, patientPayments: v.toFixed(2) };
    });
};

export const ResolveInvoiceDialog: React.FC<Props> = inject('rootStore')(
    observer((props: Props) => {
        const { rootStore, draft, onClose, onResolved } = props;
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        const { accountId } = useParams<{ accountId?: string }>();
        const store = rootStore!.collectionInvoiceStore;

        const [loading, setLoading] = useState(true);
        const [saving, setSaving] = useState(false);
        const [preview, setPreview] = useState<ProviderCollectionInvoiceDraftPreview | undefined>();
        const [lines, setLines] = useState<LineEdit[]>([]);
        const [discounts, setDiscounts] = useState<DiscountEdit[]>([]);
        const [note, setNote] = useState('');
        const [openLines, setOpenLines] = useState<Record<number, boolean>>({});
        const [accountPayments, setAccountPayments] = useState<CollectionAccountPayment[]>([]);
        const [allocById, setAllocById] = useState<Record<string, string>>({});
        const [allocOpen, setAllocOpen] = useState(false);
        const [allocDraft, setAllocDraft] = useState<Record<string, string>>({});
        const allocCardRef = useRef<HTMLDivElement>(null);
        const allocTriggerRef = useRef<HTMLElement | null>(null);

        useEffect(() => {
            let cancelled = false;
            (async () => {
                try {
                    setLoading(true);
                    const result = await store.previewDraftInvoice({
                        claimId: draft.claimId,
                        invoiceDraft: {
                            memo: draft.memo,
                            invoiceDraftItems: (draft.invoiceDraftItems || []).map(li => ({
                                serviceDate: li.serviceDate,
                                procedureCpt: li.procedureCpt,
                                procedureCptDescription: li.procedureCptDescription,
                                procedureCharge: li.procedureCharge,
                                payerPayments: li.payerPayments,
                                payerAdjustments: li.payerAdjustments,
                                patientResponsibility: li.patientResponsibility,
                                patientPayments: li.patientPayments,
                                patientDiscounts: li.patientDiscounts,
                                currentBalance: li.currentBalance,
                            })),
                            discounts: (draft.discounts || []).map(d => ({
                                serviceDate: d.serviceDate,
                                discountCpt: d.discountCpt,
                                discountCptDescription: d.discountCptDescription,
                                discountAmount: d.discountAmount,
                            })),
                        },
                    });
                    if (cancelled) return;
                    setPreview(result);
                    const previewLines = result.invoiceDraftItems || [];
                    setLines(
                        previewLines.length
                            ? previewLines.map(wrap => lineToEdit(wrap.lineItem))
                            : (draft.invoiceDraftItems || []).map(lineToEdit),
                    );
                    setDiscounts(
                        (result.discounts || draft.discounts || []).map(d => ({
                            id: nextDiscountId(),
                            serviceDate: d.serviceDate,
                            discountCpt: d.discountCpt || '00000',
                            discountCptDescription: d.discountCptDescription || '',
                            discountAmount: fromAmount(d.discountAmount),
                        })),
                    );
                    const payments = await store.loadAccountPayments(draft, result.payments);
                    if (cancelled) return;
                    setAccountPayments(payments);
                    const nextAlloc: Record<string, string> = {};
                    payments.forEach(p => {
                        nextAlloc[p.id] = (p.allocatedToThisClaim ? p.amount : 0).toFixed(2);
                    });
                    setAllocById(nextAlloc);
                } catch (err) {
                    if (!cancelled) dialogContext.showError(err);
                } finally {
                    if (!cancelled) setLoading(false);
                }
            })();
            return () => {
                cancelled = true;
            };
            // Preview once per draft open — do not depend on dialogContext (new object every
            // DialogProvider render) or the whole draft/store identity, or Confirm/Reject
            // hideDialog/reload will re-fire this POST.
        }, [draft.claimId, draft.collectionInvoiceDraftId]);

        const totals = useMemo(() => {
            const payerPayments = lines.reduce((t, l) => t + toNum(l.payerPayments), 0);
            const payerAdj = lines.reduce((t, l) => t + toNum(l.payerAdjustments), 0);
            const patResp = lines.reduce((t, l) => t + toNum(l.patientResponsibility), 0);
            const patPaid = lines.reduce((t, l) => t + toNum(l.patientPayments), 0);
            const lineDisc = lines.reduce((t, l) => t + toNum(l.patientDiscounts), 0);
            const poolDisc = discounts.reduce((t, d) => t + toNum(d.discountAmount), 0);
            const lineBalance = lines.reduce(
                (t, l) =>
                    t +
                    Math.max(
                        0,
                        toNum(l.patientResponsibility) - toNum(l.patientDiscounts) - toNum(l.patientPayments),
                    ),
                0,
            );
            const charge = lines.reduce((t, l) => t + Number(l.procedureCharge?.amount || 0), 0);
            const undistrDisc = Math.round((poolDisc - lineDisc) * 100) / 100;
            return {
                payerPayments,
                payerAdj,
                patResp,
                patPaid,
                disc: poolDisc || lineDisc,
                poolDisc,
                lineDisc,
                undistrDisc,
                balance: lineBalance,
                charge,
            };
        }, [lines, discounts]);

        /**
         * Already posted against this claim. Confirm cannot deallocate — extra dollars only
         * go out via allocatePaymentSource. Floor is p.amount for allocatedToThisClaim.
         */
        const plannedAllocations = useMemo(() => {
            const plan: { id: string; amount: number }[] = [];
            accountPayments.forEach(p => {
                const want = clampUseOnInvoice(p, toNum(allocById[p.id]));
                if (p.allocatedToThisClaim) {
                    const extra = round2(want - p.amount);
                    if (extra > 0.004) {
                        plan.push({ id: p.id, amount: round2(Math.min(extra, p.unallocatedAmount)) });
                    }
                    return;
                }
                if (want > 0.004 && p.unallocatedAmount > 0) {
                    plan.push({ id: p.id, amount: round2(Math.min(want, p.unallocatedAmount)) });
                }
            });
            return plan;
        }, [accountPayments, allocById]);

        const allocUsed = (id: string): number => {
            const p = accountPayments.find(x => x.id === id);
            if (!p) {
                return round2(toNum(allocById[id]));
            }
            return clampUseOnInvoice(p, toNum(allocById[id]));
        };

        const allocatedToInvoice = useMemo(
            () => accountPayments.filter(p => allocUsed(p.id) > 0.004),
            [accountPayments, allocById],
        );

        const availLeft = round2(
            accountPayments.reduce((t, p) => t + Math.max(0, paymentCap(p) - allocUsed(p.id)), 0),
        );

        const messages = preview?.validationMessages || draft.validationMessages || [];
        const unmatched = preview?.unmatchedPaymentItems || [];
        const wrappedLines: ProviderCollectionInvoiceDraftLineItemWithPaymentsItem[] =
            preview?.invoiceDraftItems || [];

        const issues = useMemo((): ResolveIssue[] => {
            const list: ResolveIssue[] = [];
            const push = (icon: string, label: string, detail: string, tone: IssueTone) => {
                list.push({ icon, label, detail, tone });
            };

            messages.forEach(m => {
                const text = (m.message || '').trim();
                if (!text) return;
                const lower = text.toLowerCase();
                if (lower.includes('ambiguous')) {
                    push('tune', 'Balance ambiguous after adjudication', text, 'warn');
                } else if (lower.includes('unallocated') || lower.includes('payment')) {
                    push('payments', 'Unallocated patient payments', text, 'warn');
                } else if (m.error) {
                    push('flag', 'Error', text, 'bad');
                } else {
                    push('flag', 'Needs review', text, 'warn');
                }
            });

            if (unmatched.length > 0) {
                push(
                    'link_off',
                    `${unmatched.length} posting${unmatched.length === 1 ? '' : 's'} not matched to a billed CPT`,
                    'Not tied to a service line on this invoice — review before allocating.',
                    'warn',
                );
            }

            if (Math.abs(totals.undistrDisc) > 0.004) {
                if (totals.undistrDisc > 0) {
                    push(
                        'sell',
                        `${fmtMoney(totals.undistrDisc)} of discount not distributed`,
                        'Allocate the authorized discount across the invoice lines.',
                        'warn',
                    );
                } else {
                    push(
                        'sell',
                        `${fmtMoney(Math.abs(totals.undistrDisc))} more discount applied than authorized`,
                        'Allocate the authorized discount across the invoice lines.',
                        'bad',
                    );
                }
            }

            return list;
        }, [messages, unmatched.length, totals.undistrDisc]);

        const setLine = (i: number, field: keyof LineEdit, value: string) => {
            setLines(prev => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
        };

        const setDiscount = (id: string, field: DiscountField, value: string) => {
            setDiscounts(prev => prev.map(d => (d.id === id ? { ...d, [field]: value } : d)));
        };

        const addDiscount = () => {
            setDiscounts(prev => [
                ...prev,
                {
                    id: nextDiscountId(),
                    serviceDate: lines[0]?.serviceDate || draft.invoiceDraftItems?.[0]?.serviceDate,
                    discountCpt: '00000',
                    discountCptDescription: '',
                    discountAmount: '0.00',
                },
            ]);
        };

        const removeDiscount = (id: string) => {
            setDiscounts(prev => prev.filter(d => d.id !== id));
        };

        /** Drop unposted planned amounts; keep already-posted allocations (no deallocate API). */
        const handleClearAllocation = () => {
            const next: Record<string, string> = {};
            let pool = 0;
            accountPayments.forEach(p => {
                const v = paymentFloor(p);
                next[p.id] = v.toFixed(2);
                pool = round2(pool + v);
            });
            setAllocById(next);
            setLines(prev => redistributePatientPaid(prev, pool));
        };

        const buildCreatePayload = () => {
            const invoiceDraftItems: InvoiceDraftCreateLineItem[] = lines.map(l => ({
                serviceDate: l.serviceDate,
                procedureCpt: l.procedureCpt,
                procedureCptDescription: l.procedureCptDescription,
                procedureCharge: l.procedureCharge,
                payerPayments: usd(toNum(l.payerPayments)),
                payerAdjustments: usd(toNum(l.payerAdjustments)),
                patientResponsibility: usd(toNum(l.patientResponsibility)),
                patientPayments: usd(toNum(l.patientPayments)),
                patientDiscounts: usd(toNum(l.patientDiscounts)),
                currentBalance: usd(
                    Math.max(
                        0,
                        toNum(l.patientResponsibility) - toNum(l.patientDiscounts) - toNum(l.patientPayments),
                    ),
                ),
            }));
            const discountPayload: InvoiceDraftCreateDiscount[] = discounts
                .filter(d => toNum(d.discountAmount) > 0)
                .map(d => ({
                    serviceDate: d.serviceDate,
                    discountCpt: d.discountCpt,
                    discountCptDescription: d.discountCptDescription,
                    discountAmount: usd(toNum(d.discountAmount)),
                }));
            return {
                claimId: draft.claimId,
                invoiceDraft: {
                    memo: draft.memo,
                    invoiceDraftItems,
                    discounts: discountPayload,
                },
            };
        };

        const handleConfirm = async (advance?: boolean) => {
            if (!draft.claimId) {
                toastContext.notify?.('Missing claim id', 'right');
                return;
            }
            setSaving(true);
            try {
                const claimId = draft.claimId;
                for (const a of plannedAllocations) {
                    await store.allocatePaymentSource(a.id, {
                        notes: note || undefined,
                        allocations: [{ amount: usd(a.amount), claimId }],
                    });
                }
                // Allocation changes backing payments — persist a new draft, then approve that id.
                const created = await store.createDraftInvoice(buildCreatePayload());
                const draftId = created.collectionInvoiceDraftId || draft.collectionInvoiceDraftId;
                if (!draftId) {
                    throw new Error(
                        plannedAllocations.length > 0
                            ? 'Payments were allocated to this claim, but the invoice draft came back without an id and was not queued for billing. Reopen this invoice before retrying so the payments are not applied twice.'
                            : 'The invoice draft came back without an id and was not queued for billing.',
                    );
                }
                await store.approveDraftInvoice(draftId, { notes: note || undefined });
                const originalId = draft.collectionInvoiceDraftId;
                const nextId =
                    advance && originalId ? store.nextOpenDraftId(originalId) : undefined;
                if (advance) {
                    toastContext.notify?.(
                        nextId
                            ? 'Balance confirmed — next item opened'
                            : 'Balance confirmed — queue clear',
                        'right',
                    );
                } else {
                    toastContext.notify?.('Balance confirmed — ready to bill', 'right');
                }
                await onResolved({ advance });
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setSaving(false);
            }
        };

        const handleReject = async () => {
            if (!note.trim()) {
                toastContext.notify?.('Add a note before rejecting', 'right');
                return;
            }
            if (!draft.collectionInvoiceDraftId) return;
            setSaving(true);
            try {
                await store.denyDraftInvoice(draft.collectionInvoiceDraftId, { notes: note });
                toastContext.notify?.('Rejected — removed from the queue', 'right');
                await onResolved();
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setSaving(false);
            }
        };

        const patientLabel = [draft.patientFirstName, draft.patientLastName].filter(Boolean).join(' ') || '—';
        const claimHref = draft.claimId
            ? accountId
                ? `/${accountId}/claims/${draft.claimId}`
                : `/claims/${draft.claimId}`
            : undefined;
        // The draft carries only the EMR external id. The details route resolves it to the ENTER
        // patient id and lands on `/{accountId}/patients/{patientId}/statement` (see
        // PatientDetailsRedirect) — the same statement page the claim detail page links to.
        const patientHref = draft.externalPatientId
            ? accountId
                ? `/${accountId}/patients/details/${encodeURIComponent(draft.externalPatientId)}`
                : `/patients/details/${encodeURIComponent(draft.externalPatientId)}`
            : undefined;
        const dos = lines[0]?.serviceDate || draft.invoiceDraftItems?.[0]?.serviceDate;

        const appliedTotal = round2(accountPayments.reduce((t, p) => t + allocUsed(p.id), 0));
        const headerPatient =
            lines.length > 0
                ? totals.patResp
                : Number(draft.currentBalance?.amount || 0) +
                      Number(draft.patientDiscounts?.amount || 0) +
                      Number(draft.patientPayments?.amount || 0) ||
                  Number(draft.invoiceAmount?.amount || 0);
        const headerDiscount = lines.length > 0 ? totals.disc : Number(draft.patientDiscounts?.amount || 0);
        const headerPaid =
            appliedTotal > 0
                ? appliedTotal
                : lines.length > 0
                  ? totals.patPaid
                  : Number(draft.patientPayments?.amount || 0);

        const openManageAllocation = () => {
            const draftMap: Record<string, string> = {};
            accountPayments.forEach(p => {
                draftMap[p.id] = clampUseOnInvoice(p, toNum(allocById[p.id])).toFixed(2);
            });
            setAllocDraft(draftMap);
            setAllocOpen(true);
        };

        const closeAllocOverlay = () => setAllocOpen(false);

        const saveManageAllocation = () => {
            const next: Record<string, string> = {};
            let pool = 0;
            accountPayments.forEach(p => {
                const v = clampUseOnInvoice(p, toNum(allocDraft[p.id]));
                next[p.id] = v.toFixed(2);
                pool = round2(pool + v);
            });
            setAllocById(next);
            setLines(prev => redistributePatientPaid(prev, pool));
            closeAllocOverlay();
            toastContext.notify?.(
                `${fmtMoney(pool)} of patient payments allocated — line patient-paid recalculated`,
                'right',
            );
        };

        useEffect(() => {
            if (!allocOpen) {
                return;
            }
            const root = allocCardRef.current;
            if (!root) {
                return;
            }
            allocTriggerRef.current =
                document.activeElement instanceof HTMLElement ? document.activeElement : null;

            const focusables = (): HTMLElement[] =>
                Array.from(root.querySelectorAll<HTMLElement>(ALLOC_FOCUSABLE)).filter(
                    el => el.offsetParent !== null,
                );

            const frame = window.requestAnimationFrame(() => {
                const list = focusables();
                (list[0] || root).focus();
            });

            const onKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    closeAllocOverlay();
                    return;
                }
                if (e.key !== 'Tab') {
                    return;
                }
                const list = focusables();
                if (list.length === 0) {
                    e.preventDefault();
                    root.focus();
                    return;
                }
                const first = list[0];
                const last = list[list.length - 1];
                const active = document.activeElement;
                if (e.shiftKey) {
                    if (active === first || !root.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                } else if (active === last || !root.contains(active)) {
                    e.preventDefault();
                    first.focus();
                }
            };

            document.addEventListener('keydown', onKeyDown, true);
            return () => {
                window.cancelAnimationFrame(frame);
                document.removeEventListener('keydown', onKeyDown, true);
                allocTriggerRef.current?.focus();
            };
        }, [allocOpen]);

        const issueIcon = (key: string) => {
            if (key === 'tune') return mdiTune;
            if (key === 'link_off') return mdiLinkOff;
            if (key === 'payments' || key === 'account_balance_wallet') return mdiCash;
            if (key === 'call_split') return mdiCallSplit;
            if (key === 'sell') return mdiTagOutline;
            return mdiFlagOutline;
        };

        if (loading) {
            return (
                <S.Body>
                    <S.Loading>Loading invoice preview…</S.Loading>
                </S.Body>
            );
        }

        return (
            <S.Body>
                <S.Scroll>
                <S.PatientRow>
                    <S.Avatar>
                        <Icon path={mdiInformationOutline} size={0.9} />
                    </S.Avatar>
                    <div>
                        {patientHref ? (
                            <S.PatientNameLink to={patientHref} target="_blank" rel="noopener noreferrer">
                                {patientLabel}
                                <S.PatientNameOpenIcon path={mdiOpenInNew} />
                            </S.PatientNameLink>
                        ) : (
                            <S.PatientName>{patientLabel}</S.PatientName>
                        )}
                        <S.PatientMeta>
                            {draft.externalPatientId ? <span>{draft.externalPatientId}</span> : null}
                            {draft.externalPatientId && (draft.patientDateOfBirth || claimHref) ? (
                                <S.MetaDot>·</S.MetaDot>
                            ) : null}
                            {draft.patientDateOfBirth ? (
                                <span>DOB {renderDate(draft.patientDateOfBirth)}</span>
                            ) : null}
                            {draft.patientDateOfBirth && claimHref ? <S.MetaDot>·</S.MetaDot> : null}
                            {claimHref ? (
                                <S.ClaimLink to={claimHref} target="_blank" rel="noopener noreferrer">
                                    Claim {draft.claimId}
                                </S.ClaimLink>
                            ) : null}
                        </S.PatientMeta>
                    </div>
                    <S.MetaRight>
                        DOS <S.Strong>{renderDate(dos) || '—'}</S.Strong>
                        <br />
                        {draft.primaryPayerName || '—'}
                    </S.MetaRight>
                </S.PatientRow>

                <S.AmountStrip>
                    <S.AmountStripCell>
                        <S.AmountStripLabel>Patient</S.AmountStripLabel>
                        <S.AmountStripValue>{fmtMoney(headerPatient)}</S.AmountStripValue>
                    </S.AmountStripCell>
                    <S.AmountStripCell>
                        <S.AmountStripLabel>Discount</S.AmountStripLabel>
                        <S.AmountStripValue $muted>
                            {headerDiscount > 0 ? `−${fmtMoney(headerDiscount)}` : '—'}
                        </S.AmountStripValue>
                    </S.AmountStripCell>
                    <S.AmountStripCell>
                        <S.AmountStripLabel>Patient paid</S.AmountStripLabel>
                        <S.AmountStripValue $muted>{fmtMoney(headerPaid)}</S.AmountStripValue>
                    </S.AmountStripCell>
                </S.AmountStrip>

                {issues.length > 0 ? (
                    <S.Issues>
                        <S.IssuesHeader>
                            <Icon path={mdiFlagOutline} size="15px" />
                            Needs input
                        </S.IssuesHeader>
                        {issues.map((issue, i) => (
                            <Tooltip key={i} direction="top" title={<span>{issue.detail}</span>}>
                                <S.IssueChip $tone={issue.tone}>
                                    <Icon path={issueIcon(issue.icon)} size="14px" />
                                    {issue.label}
                                </S.IssueChip>
                            </Tooltip>
                        ))}
                    </S.Issues>
                ) : null}

                <S.Discounts>
                    <S.DiscountsHeader>
                        <Icon path={mdiTagOutline} size="15px" />
                        Discounts
                        <S.PaymentsHeaderMeta>
                            Total{' '}
                            <S.PaymentsHeaderAmt>{renderAmount(usd(totals.poolDisc))}</S.PaymentsHeaderAmt>
                        </S.PaymentsHeaderMeta>
                        <S.ManageAllocButton variant="outlined" onClick={addDiscount}>
                            <Icon path={mdiPlus} size={0.7} />
                            Add discount
                        </S.ManageAllocButton>
                    </S.DiscountsHeader>
                    {discounts.length === 0 ? (
                        <S.DiscountsEmpty>No discounts on this invoice.</S.DiscountsEmpty>
                    ) : (
                        discounts.map(d => (
                            <S.DiscountRow key={d.id}>
                                <S.DiscountCodeInput
                                    value={d.discountCpt}
                                    maxLength={5}
                                    placeholder="000xx"
                                    onChange={e => setDiscount(d.id, 'discountCpt', e.target.value)}
                                />
                                <S.DiscountDescInput
                                    value={d.discountCptDescription}
                                    placeholder="Discount description"
                                    onChange={e => setDiscount(d.id, 'discountCptDescription', e.target.value)}
                                />
                                <S.DiscountAmountInput
                                    inputMode="decimal"
                                    value={d.discountAmount}
                                    onChange={e => setDiscount(d.id, 'discountAmount', e.target.value)}
                                />
                                <BaseButton variant="outlined" size="small" onClick={() => removeDiscount(d.id)}>
                                    <Icon path={mdiDeleteOutline} size={0.7} />
                                </BaseButton>
                            </S.DiscountRow>
                        ))
                    )}
                    <S.DiscountsHint>
                        Discount codes use the <strong>000xx</strong> format (00000–00099). Authorized
                        discounts here are the pool distributed across the invoice lines below.
                    </S.DiscountsHint>
                </S.Discounts>

                <S.PaymentsBlock>
                    <S.PaymentsHeader>
                        <Icon path={mdiCash} size="15px" />
                        Patient payments
                        <S.PaymentsHeaderMeta>
                            Allocated to this invoice{' '}
                            <S.PaymentsHeaderAmt>{fmtMoney(appliedTotal)}</S.PaymentsHeaderAmt>
                        </S.PaymentsHeaderMeta>
                        <S.ManageAllocButton variant="outlined" onClick={openManageAllocation}>
                            <Icon path={mdiTune} size={0.7} />
                            Manage allocation
                        </S.ManageAllocButton>
                    </S.PaymentsHeader>
                    {allocatedToInvoice.length === 0 ? (
                        <S.PaymentsEmpty>
                            No patient payments allocated to this invoice.
                            {availLeft > 0.004
                                ? ` ${fmtMoney(availLeft)} is available on this account.`
                                : ''}
                        </S.PaymentsEmpty>
                    ) : (
                        allocatedToInvoice.map(p => {
                            const used = allocUsed(p.id);
                            const still = Math.max(0, paymentCap(p) - used);
                            return (
                                <S.PaymentRow key={p.id}>
                                    <div>
                                        <S.PaymentMethod>{p.method}</S.PaymentMethod>
                                        <S.PaymentMeta>
                                            {[p.date ? renderDate(p.date) : null, p.id]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </S.PaymentMeta>
                                    </div>
                                    <S.PaymentAmount>
                                        <S.PaymentAllocated>{fmtMoney(used)}</S.PaymentAllocated>
                                        <S.PaymentBreakdown>
                                            {fmtMoney(p.sourceTotal)} total · {fmtMoney(still)} still available
                                        </S.PaymentBreakdown>
                                    </S.PaymentAmount>
                                </S.PaymentRow>
                            );
                        })
                    )}
                    {availLeft > 0.004 && (
                        <S.PaymentsAvailable>
                            <Icon path={mdiInformationOutline} size={0.7} />
                            {fmtMoney(availLeft)} of patient money on this account is available but not used
                            on this invoice.
                        </S.PaymentsAvailable>
                    )}
                </S.PaymentsBlock>

                <div>
                    <S.SectionHeader>
                        <S.SectionLabel>Invoice lines — adjust any amount; balance recalculates</S.SectionLabel>
                    </S.SectionHeader>
                    <S.TableWrap>
                        <S.Table>
                            <thead>
                                <tr>
                                    <S.Th style={{ width: 34, paddingLeft: 8, paddingRight: 0 }} />
                                    <S.Th style={{ width: 132 }}>CPT</S.Th>
                                    <S.Th>Description</S.Th>
                                    <S.Th data-right="true">Ins. Paid</S.Th>
                                    <S.Th data-right="true">Ins. Adj</S.Th>
                                    <S.Th data-right="true">Patient Resp.</S.Th>
                                    <S.Th data-right="true">Discount</S.Th>
                                    <S.Th data-right="true">Patient Paid</S.Th>
                                    <S.Th data-right="true">Balance</S.Th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l, i) => {
                                    const bal = Math.max(
                                        0,
                                        toNum(l.patientResponsibility) -
                                            toNum(l.patientDiscounts) -
                                            toNum(l.patientPayments),
                                    );
                                    const payments = wrappedLines[i]?.paymentItems || [];
                                    const expanded = !!openLines[i];
                                    const netPaid = payments.reduce(
                                        (t, p) => t + Number(p.paidAmount?.amount || 0),
                                        0,
                                    );
                                    return (
                                        <React.Fragment key={i}>
                                            <tr>
                                                <S.Td style={{ paddingLeft: 8, paddingRight: 0 }}>
                                                    <S.ExpandBtn
                                                        variant="unstyled"
                                                        onClick={() =>
                                                            setOpenLines(s => ({ ...s, [i]: !s[i] }))
                                                        }>
                                                        <Icon
                                                            path={expanded ? mdiChevronDown : mdiChevronRight}
                                                            size={0.8}
                                                        />
                                                    </S.ExpandBtn>
                                                </S.Td>
                                                <S.Td>
                                                    <S.CptCode>{l.procedureCpt || '—'}</S.CptCode>
                                                    <S.CptMeta>
                                                        {renderAmount(l.procedureCharge)}
                                                        {payments.length
                                                            ? ` · ${payments.length} posting${
                                                                  payments.length === 1 ? '' : 's'
                                                              }`
                                                            : ''}
                                                    </S.CptMeta>
                                                </S.Td>
                                                <S.Td>{l.procedureCptDescription || '—'}</S.Td>
                                                <S.Td data-right="true">
                                                    <S.AmountInput
                                                        inputMode="decimal"
                                                        value={l.payerPayments}
                                                        onChange={e => setLine(i, 'payerPayments', e.target.value)}
                                                    />
                                                </S.Td>
                                                <S.Td data-right="true">
                                                    <S.AmountInput
                                                        inputMode="decimal"
                                                        value={l.payerAdjustments}
                                                        onChange={e => setLine(i, 'payerAdjustments', e.target.value)}
                                                    />
                                                </S.Td>
                                                <S.Td data-right="true">
                                                    <S.AmountInput
                                                        inputMode="decimal"
                                                        value={l.patientResponsibility}
                                                        onChange={e =>
                                                            setLine(i, 'patientResponsibility', e.target.value)
                                                        }
                                                    />
                                                </S.Td>
                                                <S.Td data-right="true">
                                                    <S.AmountInput
                                                        inputMode="decimal"
                                                        value={l.patientDiscounts}
                                                        onChange={e => setLine(i, 'patientDiscounts', e.target.value)}
                                                    />
                                                </S.Td>
                                                <S.Td data-right="true">
                                                    <S.AmountInput
                                                        inputMode="decimal"
                                                        value={l.patientPayments}
                                                        onChange={e => setLine(i, 'patientPayments', e.target.value)}
                                                    />
                                                </S.Td>
                                                <S.Td data-right="true">
                                                    <strong style={{ fontSize: 14 }}>
                                                        {renderAmount(usd(bal))}
                                                    </strong>
                                                </S.Td>
                                            </tr>
                                            {expanded && (
                                                <tr>
                                                    <td colSpan={9} style={{ padding: 0 }}>
                                                        <S.Postings>
                                                            <S.PostingsHeader>
                                                                Payer postings for {l.procedureCpt || 'line'}
                                                                <S.PostingsHeaderMeta>
                                                                    {payments.length} · net paid{' '}
                                                                    <strong>{renderAmount(usd(netPaid))}</strong>
                                                                </S.PostingsHeaderMeta>
                                                            </S.PostingsHeader>
                                                            {payments.length === 0 ? (
                                                                <S.EmptyHint>
                                                                    <Icon path={mdiInformationOutline} size={0.7} />
                                                                    No payer posting matched to this line. Amounts
                                                                    here are entered manually.
                                                                </S.EmptyHint>
                                                            ) : (
                                                                payments.map((p, pi) => (
                                                                    <PostingView key={pi} item={p} />
                                                                ))
                                                            )}
                                                        </S.Postings>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                <tr style={{ background: Variables.ThemeLight.Background1 }}>
                                    <S.Td style={{ paddingLeft: 8 }} />
                                    <S.Td>
                                        <S.TotalRowLabel>Total</S.TotalRowLabel>
                                    </S.Td>
                                    <S.Td>
                                        <S.TotalMuted>Charges {renderAmount(usd(totals.charge))}</S.TotalMuted>
                                    </S.Td>
                                    <S.Td data-right="true">
                                        <S.TotalValue>{renderAmount(usd(totals.payerPayments))}</S.TotalValue>
                                    </S.Td>
                                    <S.Td data-right="true">
                                        <S.TotalValue>{renderAmount(usd(totals.payerAdj))}</S.TotalValue>
                                    </S.Td>
                                    <S.Td data-right="true">
                                        <S.TotalValue>{renderAmount(usd(totals.patResp))}</S.TotalValue>
                                    </S.Td>
                                    <S.Td data-right="true">
                                        <S.TotalValue>{renderAmount(usd(totals.disc))}</S.TotalValue>
                                    </S.Td>
                                    <S.Td data-right="true">
                                        <S.TotalValue>{renderAmount(usd(totals.patPaid))}</S.TotalValue>
                                    </S.Td>
                                    <S.Td data-right="true">
                                        <S.TotalBalance>{renderAmount(usd(totals.balance))}</S.TotalBalance>
                                    </S.Td>
                                </tr>
                            </tbody>
                        </S.Table>
                    </S.TableWrap>

                    {unmatched.length > 0 && (
                        <S.Orphans>
                            <S.OrphansHeader>
                                Payer postings not matched to a billed CPT
                                <span style={{ marginLeft: 'auto', fontWeight: 400, textTransform: 'none' }}>
                                    {unmatched.length}
                                </span>
                            </S.OrphansHeader>
                            {unmatched.map((p, i) => (
                                <S.OrphanRow key={i}>
                                    <strong>
                                        {[p.procedureCode, ...(p.modifierCodes || [])].filter(Boolean).join('-') ||
                                            'Claim level'}
                                    </strong>
                                    <S.PostingMeta>
                                        {renderAmount(p.paidAmount)} · {p.fromDos ? renderDate(p.fromDos) : '—'}
                                    </S.PostingMeta>
                                </S.OrphanRow>
                            ))}
                            <S.OrphansHint>
                                Pull these onto a line by editing Ins. Paid, or leave them unallocated at claim
                                level.
                            </S.OrphansHint>
                        </S.Orphans>
                    )}
                </div>

                <S.NoteArea
                    rows={2}
                    placeholder="Add a note for billing (required to reject)"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                />
                </S.Scroll>

                {allocOpen ? (
                    <S.AllocScrim
                        onMouseDown={e => {
                            if (e.target === e.currentTarget) {
                                e.preventDefault();
                                allocCardRef.current?.focus();
                            }
                        }}>
                        <S.AllocCard
                            ref={allocCardRef}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={ALLOC_TITLE_ID}
                            aria-describedby={ALLOC_DESC_ID}
                            tabIndex={-1}>
                            <S.AllocHead>
                                <div>
                                    <S.AllocTitle id={ALLOC_TITLE_ID}>Manage Payment Allocation</S.AllocTitle>
                                    <S.AllocSub id={ALLOC_DESC_ID}>
                                        Every patient payment on{' '}
                                        {draft.externalPatientId || 'this account'} — choose how much of
                                        each to use on this invoice.
                                    </S.AllocSub>
                                </div>
                                <S.AllocCloseButton
                                    variant="unstyled"
                                    aria-label="Close"
                                    onClick={closeAllocOverlay}>
                                    <Icon path={mdiClose} size={0.9} />
                                </S.AllocCloseButton>
                            </S.AllocHead>
                            <S.AllocBody>
                                <S.AllocTableWrap>
                                    <S.AllocTable>
                                        <thead>
                                            <tr>
                                                <S.AllocTh>Payment</S.AllocTh>
                                                <S.AllocTh data-right="true">Total Amount</S.AllocTh>
                                                <S.AllocTh data-right="true">Allocated Elsewhere</S.AllocTh>
                                                <S.AllocTh data-right="true">Available</S.AllocTh>
                                                <S.AllocTh data-right="true">Use On This Invoice</S.AllocTh>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accountPayments.map(p => {
                                                const cap = paymentCap(p);
                                                const floor = paymentFloor(p);
                                                const elsewhere = paymentElsewhere(p);
                                                const use = toNum(allocDraft[p.id]);
                                                const over = use - cap > 0.004;
                                                const under = floor > 0.004 && floor - use > 0.004;
                                                return (
                                                    <tr key={p.id}>
                                                        <S.AllocTd>
                                                            <S.PaymentMethod>{p.method}</S.PaymentMethod>
                                                            <S.PaymentMeta>
                                                                {[p.date ? renderDate(p.date) : null, p.id]
                                                                    .filter(Boolean)
                                                                    .join(' · ')}
                                                            </S.PaymentMeta>
                                                        </S.AllocTd>
                                                        <S.AllocTd data-right="true">
                                                            {fmtMoney(p.sourceTotal)}
                                                        </S.AllocTd>
                                                        <S.AllocTd data-right="true">{fmtMoney(elsewhere)}</S.AllocTd>
                                                        <S.AllocTd data-right="true">{fmtMoney(cap)}</S.AllocTd>
                                                        <S.AllocTd data-right="true">
                                                            <S.AllocUseCell>
                                                                <S.AllocMaxButton
                                                                    variant="outlined"
                                                                    onClick={() =>
                                                                        setAllocDraft(s => ({
                                                                            ...s,
                                                                            [p.id]: cap.toFixed(2),
                                                                        }))
                                                                    }>
                                                                    Max
                                                                </S.AllocMaxButton>
                                                                <S.AllocUseInput
                                                                    inputMode="decimal"
                                                                    $over={over || under}
                                                                    value={allocDraft[p.id] ?? '0.00'}
                                                                    onChange={e =>
                                                                        setAllocDraft(s => ({
                                                                            ...s,
                                                                            [p.id]: e.target.value,
                                                                        }))
                                                                    }
                                                                    onBlur={() =>
                                                                        setAllocDraft(s => ({
                                                                            ...s,
                                                                            [p.id]: clampUseOnInvoice(
                                                                                p,
                                                                                toNum(s[p.id]),
                                                                            ).toFixed(2),
                                                                        }))
                                                                    }
                                                                />
                                                            </S.AllocUseCell>
                                                            {over ? (
                                                                <S.AllocWarn>
                                                                    Over available {fmtMoney(cap)}
                                                                </S.AllocWarn>
                                                            ) : null}
                                                            {under ? (
                                                                <S.AllocWarn>
                                                                    Already posted {fmtMoney(floor)} — cannot
                                                                    deallocate
                                                                </S.AllocWarn>
                                                            ) : null}
                                                        </S.AllocTd>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <S.AllocTd>Total used on this invoice</S.AllocTd>
                                                <S.AllocTd data-right="true">
                                                    {fmtMoney(
                                                        accountPayments.reduce((t, p) => t + p.sourceTotal, 0),
                                                    )}
                                                </S.AllocTd>
                                                <S.AllocTd data-right="true">
                                                    {fmtMoney(
                                                        accountPayments.reduce(
                                                            (t, p) => t + paymentElsewhere(p),
                                                            0,
                                                        ),
                                                    )}
                                                </S.AllocTd>
                                                <S.AllocTd data-right="true">
                                                    {fmtMoney(
                                                        accountPayments.reduce((t, p) => t + paymentCap(p), 0),
                                                    )}
                                                </S.AllocTd>
                                                <S.AllocTd data-right="true">
                                                    <strong>
                                                        {fmtMoney(
                                                            accountPayments.reduce(
                                                                (t, p) => t + toNum(allocDraft[p.id]),
                                                                0,
                                                            ),
                                                        )}
                                                    </strong>
                                                </S.AllocTd>
                                            </tr>
                                        </tfoot>
                                    </S.AllocTable>
                                </S.AllocTableWrap>
                                <S.AllocRegenNote>
                                    <Icon path={mdiRefresh} size={0.75} />
                                    <span>
                                        Saving updates patient-paid on the invoice lines. New allocations
                                        post to the account when you confirm the balance — they are not
                                        sent twice if you save here more than once.
                                    </span>
                                </S.AllocRegenNote>
                            </S.AllocBody>
                            <S.AllocFooter>
                                <S.AllocImpact>
                                    Invoice balance after this allocation{' '}
                                    {fmtMoney(
                                        Math.max(
                                            0,
                                            totals.patResp -
                                                totals.disc -
                                                accountPayments.reduce(
                                                    (t, p) => t + toNum(allocDraft[p.id]),
                                                    0,
                                                ),
                                        ),
                                    )}
                                </S.AllocImpact>
                                <S.AllocFooterRight>
                                    <BaseButton variant="outlined" onClick={closeAllocOverlay}>
                                        Cancel
                                    </BaseButton>
                                    <BaseButton variant="primary" onClick={saveManageAllocation}>
                                        <Icon path={mdiRefresh} size={0.75} />
                                        Save allocation
                                    </BaseButton>
                                </S.AllocFooterRight>
                            </S.AllocFooter>
                        </S.AllocCard>
                    </S.AllocScrim>
                ) : null}

                <S.Footer>
                    <S.FooterCancelButton variant="outlined" onClick={onClose} disabled={saving}>
                        Cancel
                    </S.FooterCancelButton>
                    <Tooltip
                        direction="top"
                        title={
                            <span>
                                {note.trim()
                                    ? 'Remove this invoice from the queue'
                                    : 'Add a note to reject'}
                            </span>
                        }>
                        <S.FooterRejectButton variant="outlined" onClick={handleReject} disabled={saving}>
                            <Icon path={mdiCancel} size={0.75} />
                            Reject
                        </S.FooterRejectButton>
                    </Tooltip>
                    <S.FooterRight>
                        <S.FooterSecondaryButton
                            variant="outlined"
                            onClick={handleClearAllocation}
                            disabled={saving}>
                            Clear allocation
                        </S.FooterSecondaryButton>
                        <S.ConfirmNextButton
                            variant="outlined"
                            onClick={() => handleConfirm(true)}
                            disabled={saving}>
                            {saving ? 'Saving…' : 'Confirm & next'}
                            <Icon path={mdiSkipNext} size={0.75} />
                        </S.ConfirmNextButton>
                        <S.FooterConfirmButton
                            variant="primary"
                            onClick={() => handleConfirm(false)}
                            disabled={saving}>
                            {saving ? 'Saving…' : 'Confirm balance'}
                            <Icon path={mdiArrowRight} size={0.75} />
                        </S.FooterConfirmButton>
                    </S.FooterRight>
                </S.Footer>
            </S.Body>
        );
    }),
);

function lineToEdit(li?: {
    serviceDate?: string;
    procedureCpt?: string;
    procedureCptDescription?: string;
    procedureCharge?: Amount;
    payerPayments?: Amount;
    payerAdjustments?: Amount;
    patientResponsibility?: Amount;
    patientPayments?: Amount;
    patientDiscounts?: Amount;
}): LineEdit {
    return {
        serviceDate: li?.serviceDate,
        procedureCpt: li?.procedureCpt,
        procedureCptDescription: li?.procedureCptDescription,
        procedureCharge: li?.procedureCharge,
        payerPayments: fromAmount(li?.payerPayments),
        payerAdjustments: fromAmount(li?.payerAdjustments),
        patientResponsibility: fromAmount(li?.patientResponsibility),
        patientPayments: fromAmount(li?.patientPayments),
        patientDiscounts: fromAmount(li?.patientDiscounts),
    };
}

const PostingView: React.FC<{ item: ProviderClaimPaymentItem }> = ({ item }) => {
    const codeLabel = [item.procedureCode, ...(item.modifierCodes || [])].filter(Boolean).join('-');
    const adjIns = (item.adjustments || [])
        .filter(a => {
            const g = normalizePayerAdjustmentGroup(a.group);
            return isPayerAdjustmentGroupType(g) && formatPayerAdjustmentGroupShortCode(g) === 'CO';
        })
        .reduce((t, a) => t + Number(a.amount?.amount || 0), 0);
    const adjPr = (item.adjustments || [])
        .filter(a => {
            const g = normalizePayerAdjustmentGroup(a.group);
            return isPayerAdjustmentGroupType(g) && formatPayerAdjustmentGroupShortCode(g) === 'PR';
        })
        .reduce((t, a) => t + Number(a.amount?.amount || 0), 0);

    return (
        <S.PostingCard>
            <S.PostingTop>
                <div>
                    <strong>{codeLabel || '—'}</strong>
                    <S.PostingMeta>
                        Allowed {renderAmount(item.allowedAmount)} · Paid {renderDate(item.fromDos)}
                    </S.PostingMeta>
                </div>
                <S.PostingAmounts>
                    <S.PostingAmt>
                        <S.PostingAmtLabel>Allowed</S.PostingAmtLabel>
                        <S.PostingAmtValue>{renderAmount(item.allowedAmount)}</S.PostingAmtValue>
                    </S.PostingAmt>
                    <S.PostingAmt>
                        <S.PostingAmtLabel>Paid</S.PostingAmtLabel>
                        <S.PostingAmtValue>{renderAmount(item.paidAmount)}</S.PostingAmtValue>
                    </S.PostingAmt>
                    <S.PostingAmt>
                        <S.PostingAmtLabel>Ins. adj</S.PostingAmtLabel>
                        <S.PostingAmtValue>{renderAmount(usd(adjIns))}</S.PostingAmtValue>
                    </S.PostingAmt>
                    <S.PostingAmt>
                        <S.PostingAmtLabel>Patient resp.</S.PostingAmtLabel>
                        <S.PostingAmtValue>{renderAmount(usd(adjPr))}</S.PostingAmtValue>
                    </S.PostingAmt>
                </S.PostingAmounts>
            </S.PostingTop>
            {(item.adjustments || []).map((a, i) => {
                const g = normalizePayerAdjustmentGroup(a.group);
                const short = isPayerAdjustmentGroupType(g) ? formatPayerAdjustmentGroupShortCode(g) : '';
                const code = short && a.reasonCode ? `${short}-${a.reasonCode}` : a.reasonCode || '';
                const desc =
                    (a.reasonCode && AdjustmentCodes[a.reasonCode as keyof typeof AdjustmentCodes]) ||
                    a.reasonCode ||
                    '';
                return (
                    <S.Remark key={`a-${i}`}>
                        <S.RemarkCode>{code}</S.RemarkCode>
                        <span>{desc}</span>
                    </S.Remark>
                );
            })}
            {(item.remarkCodes || []).map((code, i) => (
                <S.Remark key={`r-${i}`}>
                    <S.RemarkCode>{code}</S.RemarkCode>
                    <span>{RemarkCodes[code as keyof typeof RemarkCodes] || code}</span>
                </S.Remark>
            ))}
        </S.PostingCard>
    );
};
